import Enumerable from "linq";
import { useCallback } from "react";
import { useQuery } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { UserAndRoleAdministrationRoles } from "../../services/user-role-administration-service/Models";
import UserRoleAdministrationService from "../../services/user-role-administration-service/UserRoleAdministrationService";
import { BaseUseQueryParameters } from "../BaseUseQueryParameters";
import { keys } from "./Keys";
import i18n from "../../services/localization-service/i18n";

export enum RoleSortEnum {
    NameAsc = 0,
    NameDesc = 1,
    DescriptionAsc = 2,
    DescriptionDesc = 3
}

export type UseRolesOptionsType = {
    filterText?: string;
    sortBy?: RoleSortEnum;
    excludeInternal?: boolean;
};

export type UseRolesParameters = {
    groupID: number;
    organizationID: number;
    options?: UseRolesOptionsType;
} & BaseUseQueryParameters<UseRolesReturnType>;

export type UseRolesReturnType = {
    data: UserAndRoleAdministrationRoles;
    totalCount: number;
    filteredCount: number;
};

export function useRoles({
    groupID,
    organizationID,
    options,
    enabled,
    onError,
    onSuccess
}: UseRolesParameters) {
    return useQuery<UserAndRoleAdministrationRoles, RestError, UseRolesReturnType>(
        keys.roleList(organizationID),
        ({ signal }) => {
            return UserRoleAdministrationService.getRoles(groupID, organizationID, signal);
        },
        {
            select: useCallback(
                (data: UserAndRoleAdministrationRoles) => selectRoles(data, options),
                [options]
            ),
            enabled,
            onError,
            onSuccess
        }
    );
}

function selectRoles(data: UserAndRoleAdministrationRoles, options?: UseRolesOptionsType): UseRolesReturnType {
    const roles = data.roles.map(r => {
        return {
            ...r,
            name: r.name?.length > 0
                ? r.name
                : i18n.t("noName"),
            description: r.description?.length > 0
                ? r.description
                : i18n.t("noDescription"),
        };
    });
    const filterText = options?.filterText?.toLowerCase() ?? "";

    // Filter users
    let query = filterText && filterText.length > 0
        ? Enumerable
            .from(roles)
            .where(role => {
                return (role.name?.toLowerCase().indexOf(filterText) ?? -1) !== -1 ||
                    (role.description?.toLowerCase().indexOf(filterText) ?? -1) !== -1;
            })
        : Enumerable.from(roles);

    // Sort roles
    switch (options?.sortBy) {
        case RoleSortEnum.NameAsc:
            query = query
                .orderBy(r => r.name?.toLowerCase())
                .thenBy(r => r.description?.toLowerCase());
            break;

        case RoleSortEnum.NameDesc:
            query = query
                .orderByDescending(r => r.name?.toLowerCase())
                .thenBy(r => r.description?.toLowerCase());
            break;

        case RoleSortEnum.DescriptionAsc:
            query = query
                .orderBy(r => r.description?.toLowerCase())
                .thenBy(r => r.name?.toLowerCase());
            break;

        case RoleSortEnum.DescriptionDesc:
            query = query
                .orderByDescending(r => r.description?.toLowerCase())
                .thenBy(r => r.name?.toLowerCase());
            break;
    }

    if(options?.excludeInternal){
        query = query.where(r => !r.isInternal);
    }

    const filteredAndSortedRoles = query.toArray();

    return {
        data: {
            ...data,
            roles: filteredAndSortedRoles
        },
        totalCount: roles.length,
        filteredCount: filteredAndSortedRoles.length
    };
}